import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Chat from "./Chat";

const MobileMegaMenu = ({
  categoryNav,
  mobileNavVisible,
  onClose,
  homePath,
  accountPath,
  signOutPath,
  authenticationPath,
  registrationPath,
  user,
  isAgencyUser,
  chatAvailable,
  resourceListsPath,
  myReferralsPath,
  myAgencyDataPath,
  organization,
}) => {
  const [currentView, setCurrentView] = useState("categories");
  const [selectedTopLevelIndex, setSelectedTopLevelIndex] = useState(null);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(null);
  const goToPersonas = (topLevelIndex) => {
    setSelectedTopLevelIndex(topLevelIndex);
    setSelectedCategoryIndex(null);
    setCurrentView("personas");
  };

  const goToServices = (categoryIndex) => {
    setSelectedCategoryIndex(categoryIndex);
    setCurrentView("services");
  };

  const goBack = () => {
    if (currentView === "services") {
      setCurrentView("personas");
      setSelectedCategoryIndex(null);
    } else if (currentView === "personas") {
      setCurrentView("categories");
      setSelectedTopLevelIndex(null);
    }
  };

  const resetToCategories = () => {
    setCurrentView("categories");
    setSelectedTopLevelIndex(null);
    setSelectedCategoryIndex(null);
  };

  const handleClose = () => {
    resetToCategories();
    onClose();
  };

  const handleServiceLinkClick = () => {
    handleClose();
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && mobileNavVisible) {
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileNavVisible, handleClose]);

  if (!categoryNav || categoryNav.length === 0) {
    return null;
  }

  const selectedTopLevel = selectedTopLevelIndex !== null ? categoryNav[selectedTopLevelIndex] : null;
  const categories = selectedTopLevel ? selectedTopLevel.categories : [];
  const selectedCategory = selectedCategoryIndex !== null ? categories[selectedCategoryIndex] : null;
  const groups = selectedCategory ? selectedCategory.groups : [];
  const professionalsLink = organization.navbar?.Professionals;
  const eLibraryLink = organization.navbar?.eLibrary;
  const childCareLink = organization.hero?.showChildCareResourcesLink;

  const renderToolkitSection = () => {
    const title = user ? "My 211 Account" : "Your Personal Resource Toolkit";

    return (
      <section className="mobile-mega-menu__section mobile-mega-menu__section--toolkit">
        <h3 className="mobile-mega-menu__section-title">{title}</h3>
        <div className="mobile-mega-menu__section-links">
          {user ? (
            <React.Fragment>
              <a className="mobile-mega-menu__section-link" href={accountPath} onClick={handleServiceLinkClick}>Account Settings</a>
              {isAgencyUser && (
                <a className="mobile-mega-menu__section-link" href={myAgencyDataPath} onClick={handleServiceLinkClick}>Agency</a>
              )}
              <a className="mobile-mega-menu__section-link" href={resourceListsPath} onClick={handleServiceLinkClick}>Resource Lists</a>
              {organization.userFeatures?.screeners && (
                <a className="mobile-mega-menu__section-link" href="/my211/screeners" onClick={handleServiceLinkClick}>Screeners</a>
              )}
              {organization.userFeatures?.referrals && (
                <a className="mobile-mega-menu__section-link" href={myReferralsPath} onClick={handleServiceLinkClick}>Service Requests</a>
              )}
              <a className="mobile-mega-menu__section-link" href={signOutPath} data-method="delete" onClick={handleServiceLinkClick}>Log Out</a>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <a className="mobile-mega-menu__section-link" href={registrationPath} onClick={handleServiceLinkClick}>Sign Up</a>
              <a className="mobile-mega-menu__section-link" href={authenticationPath} onClick={handleServiceLinkClick}>Login</a>
            </React.Fragment>
          )}
        </div>
      </section>
    );
  };

  const renderUtilitySection = () => (
    <section className="mobile-mega-menu__section mobile-mega-menu__section--utility">
      <div className="mobile-mega-menu__section-links">
        {professionalsLink && (
          <a className="mobile-mega-menu__section-link mobile-mega-menu__section-link--external" href={professionalsLink} target="_blank" rel="noopener noreferrer">
            Professionals <span aria-hidden="true">↗</span>
          </a>
        )}
        {eLibraryLink && (
          <a className="mobile-mega-menu__section-link mobile-mega-menu__section-link--external" href={eLibraryLink} target="_blank" rel="noopener noreferrer">
            eLibrary <span aria-hidden="true">↗</span>
          </a>
        )}
        {organization.flags?.enableChat && (
          <div className="mobile-mega-menu__section-link mobile-mega-menu__section-link--chat">
            <Chat chatAvailable={chatAvailable} organization={organization} />
          </div>
        )}
      </div>
    </section>
  );

  const renderCategoriesView = () => (
    <div className="mobile-mega-menu__panel">
      <h2 className="mobile-mega-menu__section-title">Search by Category</h2>
      <div className="mobile-mega-menu__list mobile-mega-menu__list--categories">
        {categoryNav.map((item, i) => (
          <div key={i} className="nav-item mobile-mega-menu__list-item">
            <button className="mobile-mega-menu__nav-button" onClick={() => goToPersonas(i)}>
              {item.label}
              <span className="mobile-mega-menu__arrow">&rsaquo;</span>
            </button>
          </div>
        ))}
      </div>
      {childCareLink && (
        <section className="mobile-mega-menu__section mobile-mega-menu__section--childcare">
          <h3 className="mobile-mega-menu__section-title">Looking for childcare services?</h3>
          <a className="mobile-mega-menu__childcare-link" href={childCareLink} target="_blank" rel="noopener noreferrer">
            Visit 211childcare.org <span aria-hidden="true">↗</span>
          </a>
        </section>
      )}
      {renderToolkitSection()}
      {renderUtilitySection()}
    </div>
  );

  const renderPersonasView = () => (
    <div className="mobile-mega-menu__panel">
      <div className="mobile-mega-menu__header">
        <button className="mobile-mega-menu__back" onClick={goBack}>
          &larr; View All Categories
        </button>
        <h2 className="mobile-mega-menu__title">{selectedTopLevel.label}</h2>
      </div>
      <div className="mobile-mega-menu__list mobile-mega-menu__list--personas">
        {categories.map((cat, i) => (
          <div key={i} className="nav-item mobile-mega-menu__list-item">
            <button className="mobile-mega-menu__nav-button" onClick={() => goToServices(i)}>
              {cat.label}
              <span className="mobile-mega-menu__arrow">&rsaquo;</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const renderServicesView = () => (
    <div className="mobile-mega-menu__panel">
      <div className="mobile-mega-menu__header">
        <button className="mobile-mega-menu__back" onClick={goBack}>
          &larr; {selectedTopLevel.label}
        </button>
        <h2 className="mobile-mega-menu__title">{selectedCategory.label}</h2>
      </div>
      <div className="mobile-mega-menu__services">
        {groups.map((group, i) => (
          <div key={i} className="mobile-mega-menu__service-group">
            <h3 className="mobile-mega-menu__service-heading">
              {group.view_all_href ? (
                <a
                  href={group.view_all_href}
                  className="mobile-mega-menu__service-heading-link"
                  onClick={handleServiceLinkClick}
                >
                  {group.heading}
                </a>
              ) : (
                group.heading
              )}
            </h3>
            {group.links.map((link, j) => (
              <a
                key={j}
                href={link.href}
                className="mobile-mega-menu__service-link"
                onClick={handleServiceLinkClick}
              >
                {link.label}
              </a>
            ))}
            {group.view_all_href && (
              <a
                href={group.view_all_href}
                className="mobile-mega-menu__service-link mobile-mega-menu__service-link--view-all"
                onClick={handleServiceLinkClick}
              >
                <span>View all</span>
                <span aria-hidden="true"> &rarr;</span>
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="mobile-nav mobile-mega-menu">
      {currentView === "categories" && renderCategoriesView()}
      {currentView === "personas" && renderPersonasView()}
      {currentView === "services" && renderServicesView()}
    </div>
  );
};

MobileMegaMenu.propTypes = {
  categoryNav: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string,
          groups: PropTypes.arrayOf(
            PropTypes.shape({
              heading: PropTypes.string.isRequired,
              view_all_href: PropTypes.string,
              links: PropTypes.arrayOf(
                PropTypes.shape({
                  label: PropTypes.string.isRequired,
                  href: PropTypes.string.isRequired,
                })
              ).isRequired,
            })
          ).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  mobileNavVisible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  homePath: PropTypes.string,
  accountPath: PropTypes.string,
  signOutPath: PropTypes.string,
  authenticationPath: PropTypes.string,
  registrationPath: PropTypes.string,
  user: PropTypes.string,
  isAgencyUser: PropTypes.bool,
  chatAvailable: PropTypes.bool,
  resourceListsPath: PropTypes.string,
  myReferralsPath: PropTypes.string,
  myAgencyDataPath: PropTypes.string,
  organization: PropTypes.object.isRequired,
};

export default MobileMegaMenu;
